import { Form, FormInstance } from 'antd';
import { useEffect, useRef } from 'react';

export interface ResetDependency {
  watchField: string; // campo que se observa (como empresa)
  resetFields: string[]; // campos que se deben resetear cuando cambia
}
/**
 * @description permite resetear campos de un formulario cuando cambia un campo observado.
 * @param form instancia del formulario de Ant Design
 * @param withoutInitial indica si se deben establecer los valores iniciales (para forms de creación)
 * @param dependencies lista de dependencias que contienen el campo a observar y los campos a resetear
 * @param options opciones adicionales
 * @returns un objeto con `initialValuesSetted` y `setInitialValuesSetted`
 * @example
 * ```tsx
 * const { initialValuesSetted, setInitialValuesSetted } = useFormFieldsDependences(
 *   form,
 *   { empresa: 'Empresa 1', movil: 'Movil 1' },
 *   [
 *     {
 *       watchField: 'empresa', // campo a observar
 *       resetFields: ['movil'], // campos a resetear cuando cambia 'empresa'
 *     },
 *   ],
 * );
 * ```
 */

const useFormFieldsDependences = (
  form: FormInstance | undefined,
  initialValues: Record<string, any>,
  hasInitialValuesSet: boolean,
  dependencies: ResetDependency[],
) => {
  const previousValues = useRef<Record<string, any>>(initialValues || {});
  const watchedValues = Form.useWatch([], form);

  // Reset dependencias si cambia algún campo observado
  useEffect(() => {
    if (!form || !hasInitialValuesSet) return;

    dependencies.forEach(({ watchField, resetFields }) => {
      const currentValue = watchedValues?.[watchField];
      const previousValue = previousValues.current[watchField];

      if (currentValue !== previousValue) {
        const resetValues = Object.fromEntries(resetFields.map(field => [field, undefined]));

        form.setFieldsValue(resetValues);
        previousValues.current[watchField] = currentValue;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues]);

  return {};
};

export default useFormFieldsDependences;
